import { Box, Stack, alpha } from '@mui/material';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { StoreBannersTestIds } from '../../storeBanners.testIds';
import { BannerSlide } from './bannerCarousel.types';
import BannerSlideItem from './components/BannerSlideItem';
import { useBannerCarousel } from './hooks/useBannerCarousel';

const DOT_SIZE = 8;

interface BannerCarouselProps {
  slides: BannerSlide[];
  testId?: string;
}

const BannerCarousel = ({ slides, testId }: BannerCarouselProps) => {
  const { translate } = useTranslation();
  const {
    trackRef,
    activeIndex,
    hasMultipleSlides,
    scrollToIndex,
    handleScroll,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
  } = useBannerCarousel(slides.length);

  return (
    <Box
      test-id={testId}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        borderRadius: `${theme.shape.borderRadius2xl}px`,
        backgroundColor: 'background.contrast',
      })}
    >
      <Box
        ref={trackRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {slides.map((slide) => (
          <Box
            key={slide.id}
            test-id={`${StoreBannersTestIds.SLIDE_PREFIX}${slide.id}`}
            sx={{ flex: '0 0 100%', minWidth: 0, scrollSnapAlign: 'start' }}
          >
            <BannerSlideItem slide={slide} />
          </Box>
        ))}
      </Box>
      {hasMultipleSlides && (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)' }}
        >
          {slides.map((slide, index) => (
            <Box
              key={slide.id}
              component="button"
              type="button"
              aria-label={translate(LangEnum.HOMEPAGE_BANNER_GO_TO, { position: index + 1 })}
              aria-current={index === activeIndex ? 'true' : undefined}
              test-id={`${StoreBannersTestIds.DOT_PREFIX}${slide.id}`}
              onClick={() => scrollToIndex(index)}
              sx={(theme) => ({
                width: DOT_SIZE,
                height: DOT_SIZE,
                p: 0,
                border: 'none',
                cursor: 'pointer',
                borderRadius: `${theme.shape.borderRadiusFull}px`,
                backgroundColor:
                  index === activeIndex
                    ? theme.palette.primary.main
                    : alpha(theme.palette.common.white, 0.7),
              })}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default BannerCarousel;
